import { GAME } from '../utils/christmasGame';

const R = 130; // радиус на полетата
const RIM = 14; // златната рамка
const HUB = 26;
const TEXT_R = 70; // средата на надписа
const ICON_R = 114; // иконата до ръба
const BULBS = 16;
const SLICE = 360 / GAME.segments.length;

const point = (deg, r) => {
  const rad = (deg * Math.PI) / 180;
  return [r * Math.sin(rad), -r * Math.cos(rad)];
};

const rgb = hex => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
const lighten = (hex, t) =>
  '#' + rgb(hex).map(v => Math.round(v + (255 - v) * t).toString(16).padStart(2, '0')).join('');
const luminance = hex => {
  const [r, g, b] = rgb(hex).map(v => v / 255).map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const DARK_INK = '#3b2433';
// Белият или тъмният текст – който дава по-голям контраст с полето.
const inkFor = hex => ((1.05) / (luminance(hex) + 0.05) >= (luminance(hex) + 0.05) / (luminance(DARK_INK) + 0.05) ? '#fff' : DARK_INK);

const isBig = line => /[0-9€%]/.test(line);

// Базова линия на всеки ред: голяма стойност отгоре, дума отдолу.
const layout = lines => {
  if (lines.length === 1) {
    return [{ text: lines[0], y: isBig(lines[0]) ? 6 : 5, cls: isBig(lines[0]) ? 'xg-label-big' : 'xg-label-solo' }];
  }
  return [
    { text: lines[0], y: isBig(lines[0]) ? -1 : -2, cls: isBig(lines[0]) ? 'xg-label-big' : undefined },
    { text: lines[1], y: 11, cls: undefined },
  ];
};

// Полетата са с еднакъв размер, но шансовете не са – истинските проценти са в
// таблицата под колелото.
const slices = GAME.segments.map((segment, i) => {
  const [x1, y1] = point(i * SLICE, R);
  const [x2, y2] = point((i + 1) * SLICE, R);
  return {
    ...segment,
    d: `M0 0 L${x1.toFixed(2)} ${y1.toFixed(2)} A${R} ${R} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`,
    edge: [x1, y1],
    mid: i * SLICE + SLICE / 2,
    light: lighten(segment.color, 0.15),
    ink: inkFor(segment.color),
    lines: layout(segment.label),
  };
});

const bulbs = Array.from({ length: BULBS }, (_, i) => point((i * 360) / BULBS, R + RIM / 2));

/**
 * `rotation` расте само нагоре; `stage` избира кривата на движението:
 * 'idle', 'up' – засилване, 'cruise' – чака сървъра, 'land' – спира на резултата.
 * Главината е бутон, когато `onSpin` е подаден.
 */
export const ChristmasWheel = ({ rotation, stage, onSpin }) => {
  const live = !!onSpin && stage === 'idle';
  const hubProps = live
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': 'Завърти колелото',
        onClick: onSpin,
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSpin();
          }
        },
      }
    : {};

  return (
    <div className={`xg-wheel-wrap xg-wrap-${stage}`}>
      <svg className="xg-wheel" viewBox="-150 -166 300 316" role="img" aria-label="Коледно колело">
        <defs>
          {slices.map((s, i) => (
            <radialGradient key={i} id={`xg-slice-${i}`} cx="0" cy="0" r={R} gradientUnits="userSpaceOnUse">
              <stop offset="0.2" stopColor={s.light} />
              <stop offset="0.75" stopColor={s.color} />
            </radialGradient>
          ))}
          <linearGradient id="xg-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#fbe7a1" />
            <stop offset="0.45" stopColor="#d9a93f" />
            <stop offset="1" stopColor="#8f6718" />
          </linearGradient>
          <radialGradient id="xg-hub-fill" cx="0.35" cy="0.3" r="0.8">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#efe2f3" />
          </radialGradient>
          <radialGradient id="xg-shine" cx="0" cy="-40" r="150" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#fff" stopOpacity="0.22" />
            <stop offset="0.6" stopColor="#fff" stopOpacity="0" />
          </radialGradient>
          <filter id="xg-drop" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#12071a" floodOpacity="0.5" />
          </filter>
        </defs>

        <circle r={R + RIM} fill="url(#xg-gold)" filter="url(#xg-drop)" />
        <circle r={R + 2} className="xg-rim-inner" />

        <g className={`xg-rotor xg-stage-${stage}`} style={{ transform: `rotate(${rotation}deg)` }}>
          {slices.map((s, i) => (
            <path key={`p${i}`} d={s.d} fill={`url(#xg-slice-${i})`} />
          ))}
          {slices.map((s, i) => (
            <line key={`l${i}`} x1="0" y1="0" x2={s.edge[0]} y2={s.edge[1]} className="xg-divider" />
          ))}
          {slices.map((s, i) => (
            <g key={`t${i}`} transform={`rotate(${s.mid})`}>
              <text className="xg-icon" x="0" y={-ICON_R} textAnchor="middle" dominantBaseline="central">
                {s.icon}
              </text>
              <text className="xg-label" fill={s.ink} textAnchor="middle" transform={`rotate(-90) translate(${TEXT_R} 0)`}>
                {s.lines.map((line, j) => (
                  <tspan key={j} x="0" y={line.y} className={line.cls}>{line.text}</tspan>
                ))}
              </text>
            </g>
          ))}
          <circle r={R} fill="url(#xg-shine)" pointerEvents="none" />
        </g>

        {bulbs.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3.2" className={`xg-bulb ${i % 2 ? 'xg-bulb-b' : 'xg-bulb-a'}`} />
        ))}

        <g className={`xg-hub${live ? ' xg-hub-live' : ''}`} {...hubProps}>
          <circle r={HUB + 6} className="xg-hub-halo" />
          <circle r={HUB} fill="url(#xg-hub-fill)" className="xg-hub-disc" filter="url(#xg-drop)" />
          {live
            ? <text className="xg-hub-text" textAnchor="middle" dominantBaseline="central">ВЪРТИ</text>
            : <text className="xg-hub-star" textAnchor="middle" dominantBaseline="central">★</text>}
        </g>

        <g className="xg-pointer" filter="url(#xg-drop)">
          <path d="M-12 -150 A12 12 0 1 1 12 -150 L0 -121 Z" fill="url(#xg-gold)" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
          <circle cx="0" cy="-152" r="4" fill="#fff" />
        </g>
      </svg>
    </div>
  );
};
